<template>
  <div class="mb-2 flex-grow-1 flex-shrink-1">
    <template v-if="label">
      <FieldLabel>{{ label }}</FieldLabel>
      <br>
    </template>
    <div :ref="`tags-container-${fieldName}`"
         class="tags-container d-flex align-items-center flex-wrap position-relative py-0 ps-2"
         :class="[
          !!itemList.length ? 'tags-enabled' : 'tags-disabled',
          {
            focus: focus,
            active: availableItemsListOpen || suggestionListOpen,
          }
        ]">
      <Tag v-for="item in selectedItems"
           :key="item.key"
           :item="item"
           :close="removeTag" />
      <input :ref="`tags-input-${fieldName}`"
             v-model="inputValue"
             :disabled="!itemList.length"
             class="tags-input color-sachlich-black border-0"
             :placeholder="calcPlaceholder"
             @keydown="keyHandler"
             @focus="focus = true"
             @blur="focus = false">
      <button class="button position-absolute flex-grow-0 flex-shrink-0 border-0"
              tabindex="-1"
              @click="toggleAvailableItemsList">
        <i class="fa fa-plus" />
      </button>
    </div>
    <SelectionList v-if="!!availableItems.length"
                   :field-name="fieldName"
                   :width="width()"
                   :open="availableItemsListOpen"
                   :item-list="availableItems"
                   :select-item="selectItem"
                   :highlighted-index-changed="highlightedIndexChanged" />
    <SelectionList v-if="!!suggestionItems.length"
                   :field-name="`${fieldName}-suggestion-list`"
                   :width="width()"
                   :open="suggestionListOpen"
                   :item-list="suggestionItems"
                   :select-item="selectItem"
                   :highlighted-index-changed="highlightedIndexChanged" />
  </div>
</template>

<script lang="ts">
import { IFilterKey } from '@/store/details/flex-flight/interfaces'
import Fuse from 'fuse.js'
import { cloneDeep } from 'lodash-es'
import { PropType } from 'vue'
import { KEY_CODES } from '../../general/helper'
import FieldLabel from './FieldLabel.vue'
import SelectionList from './SelectionList.vue'
import Tag from './Tag.vue'

export default {
  components: { FieldLabel, Tag, SelectionList },

  props: {
    label: { type: String, default: null },
    placeholder: { type: String, default: '' },
    fieldName: { type: String, default: '' },
    fieldValue: {
      type: Array,
      default: () => {
        return []
      },
    },
    valueChanged: { type: Function, default: () => console.log('valueChanged not implemented') }, // eslint-disable-line no-console
    itemList: {
      type: Array as PropType<any[]>,
      default: () => {
        return []
      },
    },
    maxTags: { type: Number, default: 99 }
  },

  data: () => {
    return {
      focus: false,
      availableItemsListOpen: false,
      inputValue: '',
      highlightedIndex: -1,
      fuseOptions: {
        shouldSort: true,
        threshold: 0.5,
        location: 0,
        distance: 100,
        minMatchCharLength: 1,
        isCaseSensitive: false,
        keys: ['key', 'display']
      },
    }
  },

  computed: {
    fuse() {
      return new Fuse(this.itemList, this.fuseOptions)
    },

    calcPlaceholder() {
      return this.selectedItems.length === 0 ? this.placeholder : ''
    },

    availableItems() {
      const items = []

      for (const item of this.itemList) {
        if (!this.fieldValue.includes(item.key)) {
          items.push({
            key: item.key,
            listDisplay: item.display,
          })
        }
      }
      return items
    },

    selectedItems() {
      const items = []

      for (const item of this.itemList) {
        if (this.fieldValue.includes(item.key)) {
          items.push({
            key: item.key,
            listDisplay: item.display,
          })
        }
      }
      return items
    },

    suggestionListOpen() {
      return this.inputValue.length > 0
    },

    suggestionItems() {
      const items = []
      if (this.inputValue.length > 0) {
        const fuseItems = this.fuse.search(this.inputValue)

        for (const fuseItem of fuseItems) {
          const item = fuseItem.item

          items.push({
            key: item.key,
            listDisplay: item.display
          })
        }
      }
      return items
    },
  },

  methods: {
    width() {
      const tagsContainer = this.$refs[`tags-container-${this.fieldName}`] as HTMLInputElement

      return !!tagsContainer ? tagsContainer.getClientRects()[0].width : 150
    },

    showAvailableItemsList() {
      this.availableItemsListOpen = true;
      (this.$refs[`tags-input-${this.fieldName}`] as HTMLElement).focus()
    },

    hideAvailableItemsList() {
      this.availableItemsListOpen = false
    },

    toggleAvailableItemsList() {
      this.availableItemsListOpen = !this.availableItemsListOpen;
      (this.$refs[`tags-input-${this.fieldName}`] as HTMLElement).focus()
    },

    keyHandler(event: KeyboardEvent) {
      const inputField = this.$refs[`tags-input-${this.fieldName}`] as HTMLInputElement

      switch (event.code) {
        case KEY_CODES.ARROW_DOWN:
          if (!this.suggestionListOpen) {
            this.showAvailableItemsList()
          }
          break

        case KEY_CODES.TAB:
        case KEY_CODES.ENTER:
          if (this.highlightedIndex === -1) {
            this.addTag(inputField.value.trim())
          }
          this.inputValue = ''
          break

        case KEY_CODES.BACKSPACE:
          if (this.inputValue.length === 0) {
            const index = this.selectedItems.length - 1
            const item = this.selectedItems[index]
            this.removeTag(item)
          }
          break

        case KEY_CODES.ESCAPE:
          this.hideAvailableItemsList()
          break

        default:
          if (this.suggestionListOpen) {
            this.hideAvailableItemsList()
          }
          break
      }
    },

    acceptTag(itemKey: string) {
      const candidates = this.fuse.search(itemKey)
      let candidate = !!candidates.length ? candidates[0].item : null

      if (!!candidate) {
        let accept = false

        for (const item of this.availableItems) {
          if (item.key === candidate.key) {
            accept = true
            break
          }
        }

        if (!accept) {
          candidate = null
        }
      }

      return candidate
    },

    addTag(itemKey: string) {
      const item = this.acceptTag(itemKey)

      if (item
        && !this.fieldValue.includes(item.key)
        && this.maxTags > this.fieldValue.length) {
        const fieldValue = cloneDeep(this.fieldValue)

        fieldValue.push(item.key)
        this.valueChanged(this.fieldName, fieldValue)
      }
    },

    removeTag(item: IFilterKey) {
      if (!item) return

      const index = this.fieldValue.indexOf(item.key)
      const fieldValue = cloneDeep(this.fieldValue)

      fieldValue.splice(index, 1)

      this.valueChanged(this.fieldName, fieldValue)
    },

    selectItem(item: IFilterKey) {
      if (item) {
        this.addTag(item.key)
      }
      this.inputValue = ''
      this.hideAvailableItemsList()
    },

    highlightedIndexChanged(index: number) {
      this.highlightedIndex = index
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../scss/expinet.scss';
@import '@gecko-akh/shared-styles/src/styles/AOS.vars.scss';

.tags-container {
  min-height: 32px;
  border: 2px solid $color-expinet-dark-gray;
  padding-right: 30px;
  cursor: text;
}

.tags-enabled {
  &.focus {
    border: 2px solid $color-expinet-blue;
  }

  &:hover,
  &.active {
    border: 2px solid $color-expinet-blue;

    .button {
      background-color: $color-expinet-background;
    }
  }

  .button {
    color: $color-expinet-blue;
  }
}

.tags-disabled {
  border: 2px solid $color-aida-gray;

  .input {
    cursor: default;
    background-color: $color-aida-light-gray;
    color: $color-aida-darker-gray;
  }

  .button {
    cursor: default;
    background-color: $color-aida-light-gray;
    color: $color-aida-darker-gray;
  }
}

.button {
  top: 0;
  right: 0;
  background: none;
  width: 30px;
  height: 100%;
  cursor: pointer;
  font-size: $default-font-size;

  &:focus {
    outline: none;
  }
}

.tags-input {
  box-shadow: none;
  font-size: $default-font-size;
  min-width: 20px;
  width: 100%;
  flex: 1;

  &::placeholder {
    color: $color-aida-black;
  }

  &:focus {
    outline: none;
  }
}
</style>
